<script setup lang="ts">
import { type AcceptableValue, type SelectRootEmits, type SelectRootProps, useForwardPropsEmits } from "reka-ui";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { reactiveOmit } from "@vueuse/core";
import { type HTMLAttributes, useId } from "vue";

export interface SimpleSelectProps {
  /**
   * Override where the component can find the label for an item.
   * Default: `label`
   */
  itemLabel?: string;
  /**
   * Override where the component can find the item's value.
   * Default: `value`
   */
  itemValue?: string;
  /**
   * The items available to select from.
   */
  items: Array<{ label?: string; value?: AcceptableValue; } & Record<string, unknown>>;
  placeholder?: string;
  triggerClass?: HTMLAttributes["class"];
}

const selectMenuId = useId();
const props = withDefaults(defineProps<SimpleSelectProps & SelectRootProps>(), {
  itemLabel: "label",
  itemValue: "value",
});
const emits = defineEmits<SelectRootEmits>();

const delegatedProps = reactiveOmit(props, "placeholder", "items", "itemLabel", "itemValue");
const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
  <Select v-bind="forwarded">
    <SelectTrigger :class="triggerClass">
      <SelectValue :placeholder="placeholder" />
    </SelectTrigger>

    <SelectGroup>
      <SelectContent>
        <SelectItem
          v-for="(item, index) in items"
          :key="`simple-select-${selectMenuId}-item-${index}`"
          :value="item[itemValue]!"
          :disabled="!!item.disabled"
          class="cursor-pointer"
        >
          {{ item[itemLabel] }}
        </SelectItem>
      </SelectContent>
    </SelectGroup>
  </Select>
</template>